import { Button, Descriptions, Spin } from '@surecompanies/core_components'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import Page from 'layouts/Page'
import React from 'react'
import EnrollmentsTable from '../components/EnrollmentsTable'
import OverlapPairsTable from '../components/OverlapPairsTable'
import OverlapTimeline from '../components/OverlapTimeline'
import QueryErrorState from '../components/QueryErrorState'
import SnapshotNote from '../components/SnapshotNote'
import {
  useEmployeeDetailQuery,
  useRunQuery,
} from '../hooks/useEnrollmentOverlapQueries'
import {
  employeeEnrollmentsPath,
  enrollmentOverlapPath,
  enrollmentOverlapRunPath,
} from '../paths'
import { displayEmployeeName, formatDate, orDash } from '../utils/run-display'

interface EmployeeDetailProps {
  params: { runId: string; employeePublicKey: string }
}

const SECTION_TITLE_STYLE: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 600,
  margin: '24px 0 12px',
}

export default function EmployeeDetail({ params }: EmployeeDetailProps) {
  const { runId, employeePublicKey } = params
  const { data, isLoading, error, refetch } = useEmployeeDetailQuery(
    runId,
    employeePublicKey
  )
  // Only for the "Data as of" note; the page does not depend on it loading.
  const { data: run } = useRunQuery(runId)

  const renderBody = () => {
    if (error && !data) {
      return (
        <QueryErrorState
          error={error}
          onRetry={() => void refetch()}
          notFoundTitle="Employee not found in this run"
          notFoundSubTitle="This run or employee does not exist, or the link is incorrect."
        />
      )
    }
    if (isLoading || !data) {
      return (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
        </div>
      )
    }
    return (
      <>
        <SnapshotNote startedAt={run?.startedAt} />
        <Descriptions bordered size="small" column={{ xs: 1, sm: 2, lg: 4 }}>
          <Descriptions.Item label="Employee Public Key">
            {data.employeePublicKey}
          </Descriptions.Item>
          <Descriptions.Item label="Name">
            {orDash(data.fullName)}
          </Descriptions.Item>
          <Descriptions.Item label="Overlapping pairs">
            {data.overlapCount}
          </Descriptions.Item>
          <Descriptions.Item label="Enrollments">
            {data.enrollmentCount}
          </Descriptions.Item>
          <Descriptions.Item label="Overlap range">
            {formatDate(data.firstOverlapStart)} –{' '}
            {formatDate(data.lastOverlapEnd)}
          </Descriptions.Item>
        </Descriptions>

        <h2 style={SECTION_TITLE_STYLE}>Enrollments</h2>
        <EnrollmentsTable enrollments={data.enrollments} />

        <h2 style={SECTION_TITLE_STYLE}>Overlapping pairs</h2>
        <OverlapPairsTable
          enrollments={data.enrollments}
          overlaps={data.overlaps}
        />

        <h2 style={SECTION_TITLE_STYLE}>Timeline</h2>
        <OverlapTimeline
          enrollments={data.enrollments}
          overlaps={data.overlaps}
        />
      </>
    )
  }

  return (
    <Page
      title={data ? displayEmployeeName(data) : 'Employee overlap detail'}
      action={
        data ? (
          <Button
            // type="primary"
            href={employeeEnrollmentsPath(employeePublicKey)}
            target="_blank"
            rel="noopener noreferrer"
          >
            Edit enrollments
          </Button>
        ) : undefined
      }
      subTitle={
        data ? (
          <span style={{ color: '#8c8c8c' }}>
            Employee Public Key: {data.employeePublicKey}
          </span>
        ) : undefined
      }
      breadcrumb={
        <BreadcrumbNav
          items={[
            { title: 'Home', path: '/' },
            { title: 'Enrollment Overlap', path: enrollmentOverlapPath() },
            { title: 'Run', path: enrollmentOverlapRunPath(runId) },
            { title: 'Employee' },
          ]}
        />
      }
    >
      {renderBody()}
    </Page>
  )
}
